/**
 * In-browser image pre-processing for license-plate OCR.
 *
 * Why this exists: raw camera frames at night, in shadow, or under harsh
 * Nigerian sun frequently confuse Tesseract — it sees gradients and JPEG
 * noise instead of clean letterforms. Pushing every captured frame through
 * three cheap canvas operations (grayscale → contrast stretch → adaptive
 * threshold) raises Tesseract's confidence dramatically without touching
 * the WASM bundle.
 *
 * The pipeline runs on the same hidden <canvas> that captured the frame,
 * stays under ~10 ms even for 1920×1080 input, and returns a JPEG data URL
 * ready to feed into `Tesseract.recognize`.
 *
 * - grayscale: luminance-weighted (Rec. 601: 0.299/0.587/0.114) — better
 *   contrast than the naïve mean for most plate paint colours.
 * - contrast stretch: linearly remaps the actual min/max luminance of the
 *   frame to 0–255 so we always have full dynamic range, regardless of
 *   exposure.
 * - adaptive threshold: simple Otsu via histogram peak — splits into pure
 *   black/white. The whitelist + black/white input is what Tesseract was
 *   tuned for in the Legacy + LSTM path.
 *
 * NOTE: We deliberately keep the canvas-based path (no OffscreenCanvas)
 * because Safari iOS still ships partial OffscreenCanvas support.
 */

/**
 * Downscale + JPEG-encode the current canvas contents to a compact data URL,
 * suitable for sending as Plate Pin evidence with a lookup or scan request.
 *
 * Call this AFTER `cropToPlateStrip` (so we encode only the plate region) but
 * BEFORE `preprocessForPlateOCR` (which mutates the canvas to monochrome).
 *
 * Default cap: 600 px wide. For the typical 1280×720 capture cropped to 90% × 30%
 * (1152×216), this becomes 600×112 — well under 50 KB at quality 0.7.
 */
export function captureEvidenceThumbnail(canvas, maxWidth = 600, quality = 0.7) {
  const { width, height } = canvas;
  if (!width || !height) return null;
  if (width <= maxWidth) return canvas.toDataURL("image/jpeg", quality);

  const scale = maxWidth / width;
  const tw = Math.floor(width * scale);
  const th = Math.max(1, Math.floor(height * scale));
  const tmp = document.createElement("canvas");
  tmp.width = tw;
  tmp.height = th;
  const tctx = tmp.getContext("2d");
  tctx.drawImage(canvas, 0, 0, tw, th);
  return tmp.toDataURL("image/jpeg", quality);
}

/**
 * Crop the canvas in-place to the central horizontal strip that matches the
 * yellow plate-framing guide users see in the camera UI. The guide is
 * roughly 72% of the width × ~30% of the height, vertically centred — so
 * everything outside it (sky, road, dashboard, headlights) is discarded
 * before OCR runs.
 *
 * Why this matters: Tesseract's segmentation passes spend a meaningful
 * fraction of their time deciding which blobs *might* be text. When the
 * user has pointed roughly at the plate (which the guide visually trains
 * them to do), feeding only the strip cuts noise dramatically and shaves
 * runtime ~30–40%.
 *
 * NOTE: width default reduced from 0.9 → 0.72 (-20%) to match the smaller,
 * tighter on-screen guide. A tighter crop also raises Otsu accuracy because
 * its histogram is dominated by plate pixels rather than the surrounding
 * vehicle bodywork.
 */
export function cropToPlateStrip(canvas, widthPct = 0.72, heightPct = 0.3) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width: W, height: H } = canvas;
  if (!W || !H) return;
  const cw = Math.max(1, Math.floor(W * widthPct));
  const ch = Math.max(1, Math.floor(H * heightPct));
  const ox = Math.floor((W - cw) / 2);
  const oy = Math.floor((H - ch) / 2);
  // Pull the cropped rectangle BEFORE we resize (resizing clears the canvas)
  const cropped = ctx.getImageData(ox, oy, cw, ch);
  canvas.width = cw;
  canvas.height = ch;
  // Refetch context after resize — some browsers reset state on resize
  const ctx2 = canvas.getContext("2d", { willReadFrequently: true });
  ctx2.putImageData(cropped, 0, 0);
}

/**
 * Up-scale the canvas in place so the smallest dimension is at least
 * `minHeight` pixels. Tesseract's accuracy drops sharply when text
 * height falls below ~30 px; plates cropped from 720p captures land
 * around 150–200 px tall, but mobile screens often capture at lower
 * resolutions where the cropped strip can be < 100 px. This bumps
 * those small captures to a tesseract-friendly size using bilinear
 * smoothing (the browser's default `drawImage` resample).
 *
 * No-op when the canvas is already big enough.
 */
export function upscaleForOCR(canvas, minHeight = 320) {
  const { width: W, height: H } = canvas;
  if (!W || !H) return;
  if (H >= minHeight) return;
  const scale = minHeight / H;
  const newW = Math.round(W * scale);
  const newH = minHeight;
  const tmp = document.createElement("canvas");
  tmp.width = W;
  tmp.height = H;
  tmp.getContext("2d").drawImage(canvas, 0, 0);
  canvas.width = newW;
  canvas.height = newH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(tmp, 0, 0, W, H, 0, 0, newW, newH);
}

/**
 * Return a JPEG data URL of the same canvas with each pixel inverted.
 * Used to give Tesseract a second chance on plates with light text on a
 * dark background (commercial plates, some federal plates, night shots
 * where the licence backlight inverts perceived polarity). Caller picks
 * whichever result has higher confidence.
 *
 * Does NOT mutate the canvas — only returns a data URL.
 */
export function invertedDataURL(canvas) {
  const { width, height } = canvas;
  if (!width || !height) return null;
  const tmp = document.createElement("canvas");
  tmp.width = width;
  tmp.height = height;
  const tctx = tmp.getContext("2d", { willReadFrequently: true });
  tctx.drawImage(canvas, 0, 0);
  const img = tctx.getImageData(0, 0, width, height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i]     = 255 - d[i];
    d[i + 1] = 255 - d[i + 1];
    d[i + 2] = 255 - d[i + 2];
  }
  tctx.putImageData(img, 0, 0);
  return tmp.toDataURL("image/jpeg", 0.9);
}

/**
 * Otsu's method — picks the threshold that maximizes between-class variance.
 * Pure JS, ~O(256) work after the histogram, regardless of image size.
 */
function otsuThreshold(histogram, totalPixels) {
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * histogram[i];

  let sumB = 0;
  let wB = 0;
  let maxVar = -1;
  let threshold = 127;

  for (let t = 0; t < 256; t++) {
    wB += histogram[t];
    if (wB === 0) continue;
    const wF = totalPixels - wB;
    if (wF === 0) break;
    sumB += t * histogram[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    // `>=` so that ties resolve to the highest valid threshold — important for
    // bimodal histograms where every interior t produces the same variance.
    if (between >= maxVar) {
      maxVar = between;
      threshold = t;
    }
  }
  return threshold;
}

/**
 * Pre-process the contents of `canvas` in place for plate OCR.
 * Returns a JPEG data URL of the processed image.
 *
 * The pipeline writes back to the same canvas so the user can inspect the
 * exact image the OCR engine saw (we render this in the "Last captured
 * frame" preview).
 */
export function preprocessForPlateOCR(canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width, height } = canvas;
  if (!width || !height) return canvas.toDataURL("image/jpeg", 0.9);

  const img = ctx.getImageData(0, 0, width, height);
  const data = img.data;
  const total = width * height;

  // Pass 1: convert to luminance grayscale, build histogram, track min/max
  const lum = new Uint8ClampedArray(total);
  const histogram = new Uint32Array(256);
  let minL = 255;
  let maxL = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // Rec. 601 luma — better than mean for the colours plates are usually painted on.
    const y = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) | 0;
    lum[p] = y;
    histogram[y]++;
    if (y < minL) minL = y;
    if (y > maxL) maxL = y;
  }

  // Pass 2: contrast stretch luma to full 0–255 range (rebuild histogram for Otsu)
  const range = Math.max(1, maxL - minL);
  const stretched = new Uint8ClampedArray(total);
  const stretchedHist = new Uint32Array(256);
  for (let p = 0; p < total; p++) {
    const v = ((lum[p] - minL) * 255 / range) | 0;
    stretched[p] = v;
    stretchedHist[v]++;
  }

  // Pass 3: Otsu threshold on the stretched image
  const t = otsuThreshold(stretchedHist, total);

  // Pass 4: write back as black/white pixels
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const v = stretched[p] >= t ? 255 : 0;
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    // alpha untouched
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL("image/jpeg", 0.9);
}
